import { karmaService } from './karmaService';
import { authService } from './authService';
import { databaseService } from './databaseService';
import { errorTrackingService } from './errorTrackingService';
import { feedService } from './feedService';
import { npcProfileService } from './npcProfileService';
import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabaseClient';

export interface CombatParticipant {
  id: string;
  name: string;
  type: 'player' | 'npc' | 'void_entity';
  stats: {
    hp: number;
    maxHp: number;
    karma: number;
    emptiness: number; // 0-100, affects void abilities
    energy: number;
    maxEnergy: number;
  };
  skills: CombatSkill[];
  statusEffects: StatusEffect[];
}

export interface CombatSkill {
  id: string;
  name: string;
  type: 'pure' | 'wrathful' | 'compassionate' | 'void';
  description: string;
  energyCost: number;
  karmaCost: number;
  emptinessCost?: number;
  cooldown: number;
  currentCooldown: number;
}

export interface StatusEffect {
  id: string;
  name: string;
  type: 'buff' | 'debuff' | 'karma' | 'void';
  duration: number;
  effect: string;
  value: number;
}

export interface CombatAction {
  actorId: string;
  targetIds: string[];
  skillId: string;
  timestamp: number;
  results: CombatResult[];
}

export interface CombatResult {
  targetId: string;
  damage?: number;
  healing?: number;
  karmaChange?: number;
  statusApplied?: StatusEffect;
  message: string;
}

export interface CombatSession {
  id: string;
  type: 'pve' | 'pvp' | 'raid';
  participants: CombatParticipant[];
  currentTurn: string;
  turnOrder: string[];
  combatLog: CombatAction[];
  worldState: 'balanced' | 'void_invasion' | 'time_loops';
  status: 'active' | 'victory' | 'defeat' | 'draw';
}

export class CombatService {
    private supabase: SupabaseClient;
    
    constructor() {
        this.supabase = supabaseClient;
    }
    
    getSupabase() {
        return this.supabase;
    }
  private sessions: Map<string, CombatSession> = new Map();

  /**
   * Initialize a new combat session
   */
  async startCombat(
    type: CombatSession['type'],
    playerIds: string[],
    enemyData?: Partial<CombatParticipant>[]
  ): Promise<CombatSession> {
    try {
      const sessionId = `combat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Load player data
      const participants: CombatParticipant[] = [];
      
      for (const playerId of playerIds) {
        const player = await this.loadPlayerCombatData(playerId);
        if (player) participants.push(player);
      }

      // Add NPCs/enemies if PvE
      if (type === 'pve' && enemyData) {
        const enemies = enemyData.map((data, index) => this.createEnemy(data, index));
        participants.push(...enemies);
      }

      // Get current world state
      const worldState = await this.getWorldState();

      // Create turn order based on speed/initiative
      const turnOrder = this.calculateTurnOrder(participants);

      const session: CombatSession = {
        id: sessionId,
        type,
        participants,
        currentTurn: turnOrder[0],
        turnOrder,
        combatLog: [],
        worldState,
        status: 'active'
      };

      this.sessions.set(sessionId, session);

      // Store in database for persistence
      await this.saveCombatSession(session);

      return session;
    } catch (error) {
      errorTrackingService.trackError(error as Error, {
        service: 'CombatService',
        action: 'startCombat'
      });
      throw error;
    }
  }

  /**
   * Execute a combat action with karma effects
   */
  async executeAction(
    sessionId: string,
    actorId: string,
    skillId: string,
    targetIds: string[]
  ): Promise<CombatResult[]> {
    const session = this.sessions.get(sessionId);
    if (!session || session.status !== 'active') {
      throw new Error('Invalid or inactive combat session');
    }

    // Verify it's the actor's turn
    if (session.currentTurn !== actorId) {
      throw new Error('Not your turn');
    }

    const actor = session.participants.find(p => p.id === actorId);
    const skill = actor?.skills.find(s => s.id === skillId);

    if (!actor || !skill) {
      throw new Error('Invalid actor or skill');
    }

    // Check resources
    if (actor.stats.energy < skill.energyCost) {
      throw new Error('Not enough energy');
    }

    if (skill.emptinessCost && actor.stats.emptiness < skill.emptinessCost) {
      throw new Error('Not enough emptiness');
    }

    // Apply karma effects based on Diamond Cutter Sutra
    const karmaModifier = karmaService.calculateCombatKarmaModifier(
      skill.type,
      actor.stats.karma
    );

    const results: CombatResult[] = [];

    // Process each target
    for (const targetId of targetIds) {
      const target = session.participants.find(p => p.id === targetId);
      if (!target) continue;

      const result = await this.processSkillEffect(
        actor,
        target,
        skill,
        karmaModifier,
        session
      );

      results.push(result);

      // Apply karma changes
      if (karmaModifier.karmaChange !== 0) {
        await karmaService.recordKarmaAction(
          actorId,
          'combat',
          karmaModifier.karmaChange,
          `Used ${skill.type} attack in ${session.type} combat`,
          targetIds
        );
      }
    }

    // Update actor's resources
    actor.stats.energy -= skill.energyCost;
    if (skill.emptinessCost) {
      actor.stats.emptiness -= skill.emptinessCost;
    }

    // Update skill cooldown
    skill.currentCooldown = skill.cooldown;

    // Log the action
    const action: CombatAction = {
      actorId,
      targetIds,
      skillId,
      timestamp: Date.now(),
      results
    };

    session.combatLog.push(action);

    // Check for combat end conditions
    await this.checkCombatEnd(session);

    // Advance turn
    if (session.status === 'active') {
      this.advanceTurn(session);
    }

    // Save updated session
    await this.saveCombatSession(session);

    return results;
  }

  /**
   * Process skill effects based on type and karma
   */
  private async processSkillEffect(
    actor: CombatParticipant,
    target: CombatParticipant,
    skill: CombatSkill,
    karmaModifier: ReturnType<typeof karmaService.calculateCombatKarmaModifier>,
    session: CombatSession
  ): Promise<CombatResult> {
    let damage = 0;
    let healing = 0;
    let message = '';

    // Base damage calculation
    const baseDamage = this.calculateBaseDamage(skill, actor.stats);
    
    switch (skill.type) {
      case 'pure':
        damage = baseDamage * karmaModifier.damageMultiplier;
        message = `${actor.name} strikes with pure intent`;
        break;

      case 'wrathful':
        damage = baseDamage * karmaModifier.damageMultiplier;
        // Self-corruption damage
        const selfDamage = Math.floor(damage * 0.1);
        actor.stats.hp -= selfDamage;
        message = `${actor.name} unleashes wrathful fury (self-damage: ${selfDamage})`;
        break;

      case 'compassionate':
        damage = baseDamage * karmaModifier.damageMultiplier;
        // Heal nearby allies
        healing = Math.floor(damage * 0.3);
        session.participants
          .filter(p => p.type === actor.type && p.id !== actor.id)
          .forEach(ally => {
            ally.stats.hp = Math.min(ally.stats.maxHp, ally.stats.hp + healing);
          });
        message = `${actor.name} strikes with compassion, healing allies`;
        break;

      case 'void':
        // Ignores all defenses
        damage = baseDamage * karmaModifier.damageMultiplier;
        // Costs HP
        const voidCost = Math.floor(actor.stats.maxHp * 0.1);
        actor.stats.hp -= voidCost;
        message = `${actor.name} channels the void (HP cost: ${voidCost})`;
        break;
    }

    // Apply world state modifiers
    if (session.worldState === 'void_invasion' && skill.type === 'void') {
      damage *= 1.5;
      message += ' [Void Invasion Bonus]';
    } else if (session.worldState === 'time_loops' && skill.type === 'compassionate') {
      healing *= 2;
      message += ' [Time Loop Harmony]';
    }

    // Apply damage
    target.stats.hp = Math.max(0, target.stats.hp - Math.floor(damage));

    // Check for special effects
    let statusApplied: StatusEffect | undefined;
    if (karmaModifier.sideEffect) {
      statusApplied = this.createStatusEffect(skill.type, karmaModifier.sideEffect);
      target.statusEffects.push(statusApplied);
    }

    return {
      targetId: target.id,
      damage: Math.floor(damage),
      healing,
      karmaChange: karmaModifier.karmaChange,
      statusApplied,
      message
    };
  }

  /**
   * Calculate base damage for a skill
   */
  private calculateBaseDamage(skill: CombatSkill, stats: CombatParticipant['stats']): number {
    // Base formula incorporates both energy and karma
    const karmaPower = Math.abs(stats.karma) / 10;
    const energyRatio = stats.energy / stats.maxEnergy;
    
    return 10 + (karmaPower * energyRatio * 5);
  }

  /**
   * Create status effect based on skill type
   */
  private createStatusEffect(
    skillType: CombatSkill['type'],
    effect: string
  ): StatusEffect {
    const effectMap: Record<string, Partial<StatusEffect>> = {
      'Self-corruption damage': {
        name: 'Corrupted',
        type: 'debuff',
        duration: 3,
        effect: 'Takes damage each turn',
        value: -5
      },
      'Heals nearby allies': {
        name: 'Aura of Healing',
        type: 'buff',
        duration: 2,
        effect: 'Regenerates HP',
        value: 3
      },
      'Bypasses all defenses, costs HP': {
        name: 'Void Touched',
        type: 'void',
        duration: 1,
        effect: 'Reality unstable',
        value: 0
      }
    };

    const baseEffect = effectMap[effect] || {
      name: 'Unknown Effect',
      type: 'debuff',
      duration: 1,
      effect,
      value: 0
    };

    return {
      id: `effect_${Date.now()}`,
      ...baseEffect
    } as StatusEffect;
  }

  /**
   * Load player combat data from profile
   */
  private async loadPlayerCombatData(playerId: string): Promise<CombatParticipant | null> {
    try {
      const profile = await databaseService.getUserProfile(playerId);
      if (!profile) return null;

      const karmaBalance = await karmaService.getKarmaBalance(playerId);

      // Default combat skills based on karma level
      const skills = this.getDefaultSkills(karmaBalance.level);

      return {
        id: playerId,
        name: profile.name,
        type: 'player',
        stats: {
          hp: profile.stats?.hp || 100,
          maxHp: profile.stats?.maxHp || 100,
          karma: karmaBalance.current,
          emptiness: profile.stats?.emptiness || 50,
          energy: profile.stats?.energy || 100,
          maxEnergy: profile.stats?.maxEnergy || 100
        },
        skills,
        statusEffects: []
      };
    } catch (error) {
      console.error('Failed to load player combat data:', error);
      return null;
    }
  }

  /**
   * Get default skills based on karma level
   */
  private getDefaultSkills(karmaLevel: string): CombatSkill[] {
    const baseSkills: CombatSkill[] = [
      {
        id: 'pure_strike',
        name: 'Pure Strike',
        type: 'pure',
        description: 'A balanced attack with no karma cost',
        energyCost: 10,
        karmaCost: 0,
        cooldown: 0,
        currentCooldown: 0
      }
    ];

    // Add skills based on karma level
    switch (karmaLevel) {
      case 'enlightened':
      case 'light':
        baseSkills.push({
          id: 'compassionate_wave',
          name: 'Compassionate Wave',
          type: 'compassionate',
          description: 'Damage enemies while healing allies',
          energyCost: 20,
          karmaCost: 0,
          cooldown: 2,
          currentCooldown: 0
        });
        break;

      case 'dark':
      case 'corrupted':
        baseSkills.push({
          id: 'wrathful_blow',
          name: 'Wrathful Blow',
          type: 'wrathful',
          description: 'Double damage at the cost of karma and self-harm',
          energyCost: 15,
          karmaCost: -5,
          cooldown: 1,
          currentCooldown: 0
        });
        break;

      case 'balanced':
        baseSkills.push({
          id: 'void_step',
          name: 'Void Step',
          type: 'void',
          description: 'Phase through defenses using emptiness',
          energyCost: 25,
          karmaCost: 0,
          emptinessCost: 10,
          cooldown: 3,
          currentCooldown: 0
        });
        break;
    }

    return baseSkills;
  }

  /**
   * Create an enemy combatant
   */
  private createEnemy(
    data: Partial<CombatParticipant>,
    index: number
  ): CombatParticipant {
    const enemyTypes = {
      void_wraith: {
        name: 'Void Wraith',
        stats: { hp: 50, maxHp: 50, karma: -30, emptiness: 80, energy: 50, maxEnergy: 50 },
        skills: [{
          id: 'void_drain',
          name: 'Void Drain',
          type: 'void' as const,
          description: 'Drains life force',
          energyCost: 10,
          karmaCost: 0,
          cooldown: 1,
          currentCooldown: 0
        }]
      },
      corrupted_ai: {
        name: 'Corrupted AI',
        stats: { hp: 80, maxHp: 80, karma: -50, emptiness: 20, energy: 80, maxEnergy: 80 },
        skills: [{
          id: 'data_corruption',
          name: 'Data Corruption',
          type: 'wrathful' as const,
          description: 'Corrupts target data',
          energyCost: 15,
          karmaCost: -2,
          cooldown: 2,
          currentCooldown: 0
        }]
      },
      lost_dreamer: {
        name: 'Lost Dreamer',
        stats: { hp: 60, maxHp: 60, karma: 0, emptiness: 100, energy: 60, maxEnergy: 60 },
        skills: [{
          id: 'dream_shift',
          name: 'Dream Shift',
          type: 'pure' as const,
          description: 'Shifts between realities',
          energyCost: 20,
          karmaCost: 0,
          cooldown: 1,
          currentCooldown: 0
        }]
      }
    };

    const enemyType = Object.values(enemyTypes)[index % Object.keys(enemyTypes).length];
    
    return {
      id: `enemy_${index}_${Date.now()}`,
      type: 'npc',
      ...enemyType,
      ...data,
      statusEffects: []
    };
  }

  /**
   * Calculate turn order based on initiative
   */
  private calculateTurnOrder(participants: CombatParticipant[]): string[] {
    return participants
      .map(p => ({
        id: p.id,
        initiative: p.stats.energy + (p.stats.emptiness / 10)
      }))
      .sort((a, b) => b.initiative - a.initiative)
      .map(p => p.id);
  }

  /**
   * Advance to next turn
   */
  private advanceTurn(session: CombatSession): void {
    const currentIndex = session.turnOrder.indexOf(session.currentTurn);
    const nextIndex = (currentIndex + 1) % session.turnOrder.length;
    session.currentTurn = session.turnOrder[nextIndex];

    // Process status effects
    session.participants.forEach(participant => {
      participant.statusEffects = participant.statusEffects
        .map(effect => ({ ...effect, duration: effect.duration - 1 }))
        .filter(effect => effect.duration > 0);

      // Reduce cooldowns
      participant.skills.forEach(skill => {
        if (skill.currentCooldown > 0) {
          skill.currentCooldown--;
        }
      });
    });
  }

  /**
   * Check if combat should end
   */
  private async checkCombatEnd(session: CombatSession): Promise<void> {
    const activePlayers = session.participants.filter(
      p => p.type === 'player' && p.stats.hp > 0
    );
    const activeEnemies = session.participants.filter(
      p => p.type !== 'player' && p.stats.hp > 0
    );

    if (activePlayers.length === 0) {
      session.status = 'defeat';
      // Record karma loss for defeat
      for (const player of session.participants.filter(p => p.type === 'player')) {
        await karmaService.recordKarmaAction(
          player.id,
          'combat',
          -10,
          'Defeated in combat',
          []
        );
        
        // Create feed post for defeat
        await feedService.createCombatResultPost(
          player.id,
          player.name,
          'player',
          {
            outcome: 'defeat',
            opponent: activeEnemies[0]?.name || 'Unknown Enemy',
            karmaGained: -10,
            experienceGained: 0
          }
        );
      }
    } else if (activeEnemies.length === 0 && session.type === 'pve') {
      session.status = 'victory';
      // Record karma gain for victory
      for (const player of activePlayers) {
        await karmaService.recordKarmaAction(
          player.id,
          'combat',
          5,
          'Victory in combat',
          []
        );
        
        // Create feed post for victory
        const defeatedEnemies = session.participants.filter(p => p.type !== 'player');
        const experienceGained = Math.floor(defeatedEnemies.length * 25);
        
        await feedService.createCombatResultPost(
          player.id,
          player.name,
          'player',
          {
            outcome: 'victory',
            opponent: defeatedEnemies.map(e => e.name).join(', '),
            karmaGained: 5,
            experienceGained
          }
        );
        
        // Update player experience if they have a profile
        if (player.type === 'player') {
          // This would need to be integrated with player profile service
          console.log(`[CombatService] Player ${player.name} gained ${experienceGained} experience`);
        }
      }
      
      // Create feed posts for defeated NPCs
      for (const npc of session.participants.filter(p => p.type === 'npc' && p.stats.hp <= 0)) {
        await feedService.createCombatResultPost(
          npc.id,
          npc.name,
          'npc',
          {
            outcome: 'defeat',
            opponent: activePlayers.map(p => p.name).join(', '),
            karmaGained: -5,
            experienceGained: 0
          }
        );
        
        // Update NPC experience
        await npcProfileService.updateKarma(npc.id, -5, 'Defeated in combat');
      }
    } else if (session.type === 'pvp' && activePlayers.length === 1) {
      session.status = 'victory';
      const winner = activePlayers[0];
      await karmaService.recordKarmaAction(
        winner.id,
        'combat',
        10,
        'PvP victory',
        session.participants.filter(p => p.id !== winner.id).map(p => p.id)
      );
    }
  }

  /**
   * Get current world state
   */
  private async getWorldState(): Promise<CombatSession['worldState']> {
    try {
      const { data } = await this.supabase
        .from('world_state')
        .select('state')
        .eq('id', 'current')
        .single();

      return data?.state || 'balanced';
    } catch {
      return 'balanced';
    }
  }

  /**
   * Save combat session to database
   */
  private async saveCombatSession(session: CombatSession): Promise<void> {
    try {
      await this.supabase
        .from('combat_sessions')
        .upsert({
          id: session.id,
          type: session.type,
          participants: session.participants,
          current_turn: session.currentTurn,
          turn_order: session.turnOrder,
          combat_log: session.combatLog,
          world_state: session.worldState,
          status: session.status,
          updated_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('Failed to save combat session:', error);
    }
  }

  /**
   * Get active combat session for a player
   */
  async getPlayerCombatSession(playerId: string): Promise<CombatSession | null> {
    try {
      const { data } = await this.supabase
        .from('combat_sessions')
        .select('*')
        .eq('status', 'active')
        .contains('participants', [{ id: playerId }])
        .single();

      if (!data) return null;

      const session: CombatSession = {
        id: data.id,
        type: data.type,
        participants: data.participants,
        currentTurn: data.current_turn,
        turnOrder: data.turn_order,
        combatLog: data.combat_log,
        worldState: data.world_state,
        status: data.status
      };

      this.sessions.set(session.id, session);
      return session;
    } catch (error) {
      console.error('Failed to get player combat session:', error);
      return null;
    }
  }
}

// Export singleton instance
export const combatService = new CombatService();